/* eslint-disable max-len */
import { useState, useEffect, useRef } from 'react';

import Cookies from 'js-cookie';
import queryString from 'query-string';
import EnvoyClient from 'shopify-storefront-client';
import aa from 'search-insights';
import { gtmAddToCart } from '../enhanced-ecommerce';
import { useLocalStorage } from '../hooks';
import { getSearchClickContext, getAlgoliaCartAttributes, ALGOLIA_CART_ATTRIBUTE_PREFIX } from '../../components/SearchAlgolia/helpers/algolia-functions';
import {
	getProductVariantById, getProductVariantsByIds, getEventClassesAvailability, isColorSwatchesProduct, isEducationProductType
} from '..';

//	Fires an Algolia Insights "Product Added To Cart" conversion, but only
//	when this add is actually related to a search: i.e. a stored click
//	context exists, is still fresh, and was for this same Shopify product.
//	The index de-duplicates variants down to one record per product, so
//	the objectID sent is always the one stored from the click -- whichever
//	variant the shopper ends up adding.
const sendAlgoliaAddToCartConversions = items => {
	items.forEach(item => {
		const productId = item.productId?.split('/').pop();
		if (!productId) return;

		const searchContext = getSearchClickContext(productId);
		if (!searchContext) return;

		const price = item.price?.amount ? Number(item.price.amount) : undefined;

		aa('addedToCartObjectIDsAfterSearch', {
			eventName: 'Product Added To Cart',
			index: searchContext.index,
			queryID: searchContext.queryID,
			objectIDs: [searchContext.objectID],
			objectData: [{
				price,
				quantity: item.quantity,
			}],
			value: price ? price * (item.quantity || 1) : undefined,
			currency: 'USD',
		});
	});
};

const envoyClient = EnvoyClient.fromSettings({
	shop: {
		myshopify_domain: process.env.GATSBY_SHOPIFY_STORE_URL,
	},
	api: {
		token: process.env.GATSBY_STOREFRONT_ACCESS_TOKEN,
		version: process.env.GATSBY_SHOPIFY_API_VERSION,
	},
});

//	shopify-storefront-client@1.2.0 (latest) declares the cartDiscountCodesUpdate
//	variable as nullable (`$discountCodes: [String!]`). Shopify makes that argument
//	required in API 2026-01 (older versions retired Oct 1, 2026), which rejects the
//	query. We can't override the query per-call, but updateDiscountCodes reads this
//	mutation string at call time, so patch it to non-null here (app-side, no fork /
//	patch-package). Our callers always pass a non-null array, so nothing else changes.
if (envoyClient?.cart?.cartDiscountCodesUpdateMutation) {
	envoyClient.cart.cartDiscountCodesUpdateMutation = envoyClient.cart.cartDiscountCodesUpdateMutation
		.replace(/(\$discountCodes:\s*\[String!\])(?!!)/, '$1!');
}

export const localStorageCheckoutKey = `shopify_checkout_id`;
export const localStorageClassCheckoutKey = `shopify_class_checkout_id`;

const defaultValues = {
	checkout: {
		lineItems: [],
		totalQuantity: 0,
	},
};

const useCheckout = ({
	isCartFlyoutOpen,
	cartFlyoutOpen,
	setCartError,
}) => {
	// Checkout loading state (true means loading, false means loaded)
	const [checkoutLoadingState, setCheckoutLoadingState] = useState({
		[localStorageCheckoutKey]: true,
		[localStorageClassCheckoutKey]: true,
	});
	const [checkout, setCheckout] = useState({ ...defaultValues.checkout });
	const [classCheckout, setClassCheckout] = useState({ ...defaultValues.checkout });
	const [newCheckoutAttributes, setCheckoutAttributes] = useState(null);
	//	Signals which cart the most recent add went to, so the flyout can
	//	activate the matching tab ('products' or 'classes'). New ref each add.
	const [lastAdded, setLastAdded] = useState(null);

	const checkoutLoading = Object.values(checkoutLoadingState).some(Boolean);

	// Color swatches local storage
	const { value: colorSwatchesLS, setValue: setColorSwatchesLS } = useLocalStorage(
		// Key
		'color-swatches-products-in-cart',
		// Initial value
		{ cartId: '', lineItems: [] },
		// Callback function
		() => {
			setTimeout(() => {
				updateLineItem(checkout.id, []); // eslint-disable-line
			}, 200);
		}
	);

	// Color swatches effect
	useEffect(() => {
		if (!checkout.id) return;

		const colorSwatchesLineItemsInCart = checkout.lineItems
			.filter(item => isColorSwatchesProduct(item.variant?.product))
			.map(item => ({...item, paymentStatus: 'pending'}));

		if (colorSwatchesLS.cartId === checkout.id) {
			setColorSwatchesLS(prev => {
				const paidLineItems = prev.lineItems.filter(item => item.paymentStatus === 'paid');
				return {
					...prev,
					lineItems: [...paidLineItems, ...colorSwatchesLineItemsInCart],
				};
			});
		} else {
			setColorSwatchesLS(prev => {
				const paidLineItems = prev.lineItems.map(item => ({...item, paymentStatus: 'paid'}));
				return {
					...prev,
					cartId: checkout.id,
					lineItems: [...paidLineItems, ...colorSwatchesLineItemsInCart],
				};
			});
		}
	}, [checkout.lineItems.length, checkout.id]); //eslint-disable-line

	// Draft orders local storage
	const { value: draftOrdersIds, setValue: setDraftOrdersIds } = useLocalStorage('draftOrdersIds', []);

	// draft orders effect
	const isDraftChecking = useRef(false);

	useEffect(() => {
		// 1. EXIT if the flyout is closed or there are no IDs
		if (draftOrdersIds.length === 0) return;

		const checkDrafts = async() => {
			// Only check if the user is looking at the page
			if (document.hidden || isDraftChecking.current) return;

			// 2. PREVENT MULTIPLE api calls with a ref flag
			isDraftChecking.current = true;

			try {
				const results = await Promise.all(
					draftOrdersIds.map(async id => {
						const res = await fetch(`${ process.env.GATSBY_BB_CHECKOUT_BASE_URL }?id=${ id }`);
						const data = await res.json();
						return (data?.draftOrder?.status === 'COMPLETED' || data?.draftOrder?.completedAt || !data?.draftOrder) ? null : id;
					})
				);

				const activeIds = results.filter(Boolean);

				// This IF is the most important line to prevent infinite loops
				setDraftOrdersIds(prev => {
					return activeIds.length !== prev.length ? activeIds : prev;
				});
			} catch (err) {
				window.console.error(err);
			} finally {
				isDraftChecking.current = false;
			}
		};

		// 2. TRIGGER IMMEDIATELY when flyout opens and set INTERVAL only while flyout is open
		let interval;
		if (isCartFlyoutOpen) {
			checkDrafts(); // Trigger immediately on open
			interval = setInterval(checkDrafts, 5000);
		}

		// 3. HANDLE VISIBILITY CHANGE (Listen for tab focus)
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				checkDrafts();
			}
		};

		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			if (interval) clearInterval(interval);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
		};
	}, [isCartFlyoutOpen, draftOrdersIds]); // eslint-disable-line

	const getCheckoutSetterAndKey = ({ checkoutId, storageKey }) => {
		let setter = setCheckout;
		let key = localStorageCheckoutKey;

		if (storageKey) {
			switch (storageKey) {
			case localStorageCheckoutKey:
				setter = setCheckout;
				key = localStorageCheckoutKey;
				break;
			case localStorageClassCheckoutKey:
				setter = setClassCheckout;
				key = localStorageClassCheckoutKey;
				break;
			default:
				setter = setCheckout;
				key = localStorageCheckoutKey;
			}
		} else if (checkoutId) {
			if (checkoutId === localStorage.getItem(localStorageCheckoutKey)) {
				setter = setCheckout;
				key = localStorageCheckoutKey;
			} else if (checkoutId === localStorage.getItem(localStorageClassCheckoutKey)) {
				setter = setClassCheckout;
				key = localStorageClassCheckoutKey;
			}
		}

		return { setter, storageKey: key };
	};

	const setCheckoutItem = async({
		checkoutItem,
		storageKey = localStorageCheckoutKey,
	} = {}) => {
		const checkoutId = localStorage.getItem(storageKey);
		if (!checkoutId && !checkoutItem) throw new Error('No checkoutId or checkoutItem provided');

		const { setter } = getCheckoutSetterAndKey({ storageKey });

		const setAndStore = item => {
			localStorage.setItem(storageKey, item.id);
			setter(() => item);
			return item;
		};

		try {
			if (checkoutItem) return setAndStore(checkoutItem);

			const checkoutResponse = await envoyClient.cart.lineItems.update(checkoutId);

			if (!checkoutResponse || checkoutResponse.userErrors?.length) throw checkoutResponse;
			return setAndStore(checkoutResponse);
		} catch (error) {
			window.console.error(error);
			localStorage.removeItem(storageKey);
			setter(() => ({ ...defaultValues.checkout }));
			return { error };
		}
	};

	const removeLineItems = async(checkoutId, lineItemID = []) => {
		const { setter, storageKey } = getCheckoutSetterAndKey({ checkoutId });
		setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: true }));
		return envoyClient.cart.lineItems
			.remove(checkoutId, [...lineItemID])
			.then(res => {
				setter(() => res);
				setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: false }));
			});
	};

	async function updateLineItem(checkoutId, lineItem = [/* { id, merchandiseId, quantity, attributes } */]) {
		const { setter, storageKey } = getCheckoutSetterAndKey({ checkoutId });
		setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: true }));

		return envoyClient.cart.lineItems
			.update(checkoutId, lineItem)
			.then(res => {
				setter(() => res);
				setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: false }));
			});
	}

	const updateLineItems = async(
		checkoutId,
		lineItemsToUpdate = [/* {id, merchandiseId, quantity, attributes} */]
	) => {
		const { setter, storageKey } = getCheckoutSetterAndKey({ checkoutId });
		setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: true }));

		return envoyClient.cart.lineItems
			.update(checkoutId, lineItemsToUpdate)
			.then(async res => {
				setter(() => res);
				setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: false }));
			});
	};

	const duplicateClassItem = (item, storageKey) => {
		if (storageKey !== localStorageClassCheckoutKey) {
			if (item.productType !== 'course_manual') return false;
			// Check if physical class addon is already in cart
			return checkout.lineItems.some(lineItem => {
				const isAddonInCart = lineItem.variant.id === item.variantId;
				return isAddonInCart;
			});
		}
		// Check if class item is already in cart
		return classCheckout.lineItems.some(lineItem => {
			const isClassInCart = lineItem.variant.id === item.variantId;
			const isEmailInCart = item.customAttributes.find(attr => attr.key === '_attendee_email')?.value === lineItem.attributes.find(attr => attr.key === '_attendee_email')?.value;
			return isClassInCart && isEmailInCart;
		});
	};

	//	Events always live in the regular checkout (never the class checkout).
	//	If any incoming item is tagged with an _event_product_id, return the
	//	ids of that event's existing line items so a re-add replaces its
	//	previous selection (main conference + pre/post classes) instead of
	//	duplicating it. Non-event adds carry no tag and yield an empty list.
	const replaceableEventLineItems = items => {
		const incomingEventProductIds = [...new Set(
			items
				.map(item => item.customAttributes?.find(attr => attr.key === '_event_product_id')?.value)
				.filter(Boolean)
		)];

		if (!incomingEventProductIds.length) return [];

		return checkout.lineItems
			.filter(lineItem => {
				const lineItemEventId = lineItem.attributes?.find(attr => attr.key === '_event_product_id')?.value;
				return lineItemEventId && incomingEventProductIds.includes(lineItemEventId);
			})
			.map(lineItem => lineItem.id);
	};

	const addVariantsToCart = async({
		checkoutId = checkout.id,
		items = [/* { productId, variantId, sku, quantity, customAttributes, productType, productTitle, variantTitle, price: {amount, currency} } */],
		errors = [],
		cartFlyoutOn = true,
	}) => {
		if (errors.length) {
			return setCartError({
				visible: true,
				errorEmail: errors.join('%0D%0A %0D%0A'),
			});
		}

		const { setter, storageKey } = getCheckoutSetterAndKey({ checkoutId });

		//	Replace-by-event: remove the event's existing line items (regular
		//	checkout) before adding, so a re-add replaces the previous selection.
		const eventLineItemIdsToReplace = replaceableEventLineItems(items);
		if (eventLineItemIdsToReplace.length) {
			await removeLineItems(checkout.id, eventLineItemIdsToReplace);
		}

		const lineItemsToAdd = items
			.filter(itemToAdd => !duplicateClassItem(itemToAdd, storageKey))
			.map(({
				variantId, sku, quantity, customAttributes = [], productType, productId,
			}) => {
				const attributes = [...customAttributes]
					.filter(attrib => attrib.key !== '_AID' && !attrib.key.startsWith(ALGOLIA_CART_ATTRIBUTE_PREFIX));

				// Add part number to custom attribs
				attributes.unshift({
					key: 'PN',
					value: sku,
				});
				// Add affiliate value to custom attribs
				attributes.push({
					key: '_AID',
					value: Cookies.get('affiliate') || 'null',
				});

				// Tag educational productTypes (class, event, course_manual, streaming_video,
				// access, etc.) so Shopify orders/exports can distinguish them. Only added
				// when a caller hasn't already set this attribute explicitly (e.g. the
				// magic-URL bulk checkout flow, which can set it per-line-item).
				if (!attributes.some(attrib => attrib.key === '_is_education_order') && isEducationProductType(productType)) {
					attributes.push({
						key: '_is_education_order',
						value: 'true',
					});
				}

				// Search attribution for the Shopify checkout pixel (only when this add
				// follows a search click on this product).
				attributes.push(...getAlgoliaCartAttributes(productId?.split('/').pop()));

				return {
					merchandiseId: variantId,
					quantity: parseInt(quantity, 10),
					attributes,
				};
			});

		setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: true }));

		return envoyClient.cart.lineItems
			.add(checkoutId, lineItemsToAdd)
			.then(async res => {
				if (res.userErrors?.length)	throw new Error(JSON.stringify(res.userErrors));
				setter(() => res);
				setLastAdded({
					type: storageKey === localStorageClassCheckoutKey ? 'classes' : 'products',
					at: Date.now(),
				});
				gtmAddToCart(items);
				sendAlgoliaAddToCartConversions(items);
			})
			.catch(err => {
				window.console.error(err);
				setCartError({
					visible: true,
					errorEmail: err.message,
				});
			})
			.finally(() => {
				setCheckoutLoadingState(prev => ({ ...prev, [storageKey]: false }));
				cartFlyoutOn && cartFlyoutOpen();
			});
	};

	const checkEventClassesAvailability = async() => {
		const data = await getEventClassesAvailability(checkout.lineItems);
		const { eventLineItemsIDs, eventClassesData, allEventClassesAvailable } = data;

		let isSoldoutClasses = false;

		if (eventLineItemsIDs.length && eventClassesData.length && !allEventClassesAvailable) {
			await removeLineItems(checkout.id, eventLineItemsIDs);
			isSoldoutClasses = true;
			setCartError({
				visible: true,
				errorHeading: 'Event class no longer available',
				errorBody: `One or more popular classes has sold out! We've removed the event from your cart. Reload the event registration page to find another class.`,
				errorEmail: '',
			});
		}

		return {isSoldoutClasses};
	};

	const initializeCheckoutGeneric = async(options = {}) => {
		const { storageKey } = options;

		const storedCheckoutID = localStorage.getItem(storageKey) || null;

		if (storedCheckoutID && storedCheckoutID !== `null`) {
			const { error } = await setCheckoutItem({ storageKey });
			if (error) return initializeCheckoutGeneric({ storageKey });
			return;
		}

		const newCheckout = await envoyClient.cart.create();
		await setCheckoutItem({ checkoutItem: newCheckout, storageKey });
	};

	//	---------------------
	//	Initialize checkout
	//	---------------------
	useEffect(() => {
		Promise.all([
			initializeCheckoutGeneric({
				storageKey: localStorageCheckoutKey,
			}),
			initializeCheckoutGeneric({
				storageKey: localStorageClassCheckoutKey,
			}),
		])
			.finally(() => {
				setCheckoutLoadingState(() => ({ localStorageCheckoutKey: false, localStorageClassCheckoutKey: false }));
			});
	}, []); // eslint-disable-line

	//	------------------------------------------------------------------
	//	Re-validate checkout on back/forward-cache (bfcache) restore
	//	------------------------------------------------------------------
	//	After a Shopify hosted checkout completes and the user presses the
	//	browser Back button, the page is restored from an in-memory
	//	snapshot (bfcache). React is not remounted, so the mount-time
	//	initialization effect above never re-runs and the cart flyout keeps
	//	showing items that were already purchased. Re-fetching the cart here
	//	fixes it.
	useEffect(() => {
		const handlePageShow = event => {
			// Only react to bfcache restores; a normal (re)load already runs
			// the initialization effect above.
			if (!event.persisted) return;

			setCheckoutLoadingState(() => ({
				[localStorageCheckoutKey]: true,
				[localStorageClassCheckoutKey]: true,
			}));

			Promise.all([
				initializeCheckoutGeneric({ storageKey: localStorageCheckoutKey }),
				initializeCheckoutGeneric({ storageKey: localStorageClassCheckoutKey }),
			])
				.finally(() => {
					setCheckoutLoadingState(() => ({
						[localStorageCheckoutKey]: false,
						[localStorageClassCheckoutKey]: false,
					}));
				});
		};

		window.addEventListener('pageshow', handlePageShow);
		return () => window.removeEventListener('pageshow', handlePageShow);
	}, []); // eslint-disable-line

	//	----------------------------------
	//	Add attribs to the checkout item
	//	----------------------------------
	useEffect(() => {
		if (checkout.id && newCheckoutAttributes) {
			// check if new checkoutAttributes is already existing or not.
			const isUpdateCheckoutAttribs = !(newCheckoutAttributes.every(({ key, value }) => {
				return checkout.attributes
					.find(attr => attr.key === key)
					?.value === value;
			}));

			isUpdateCheckoutAttribs
			&& envoyClient.cart
				.updateAttributes(checkout.id, newCheckoutAttributes)
				.then(res => setCheckout(() => res))
				.catch(e => window.console.error(e));
		}
	}, [checkout.id, newCheckoutAttributes]); // eslint-disable-line

	//	-----------------------------------------
	//	Add product to checkout if params in URL
	//	-----------------------------------------
	useEffect(() => {
		if (window.location.pathname === '/' && window.location.search && checkout.id) {
			const { id, quantity = 1 } = queryString.parse(window.location.search);
			if (!id) return;
			const variantId = `gid://shopify/ProductVariant/${ id }`;
			getProductVariantById(variantId)
				.then(async variant => {
					if (!variant) {
						throw new Error(`
							Page: ${ window.location.href.replace('&', ' and ') } %0D%0A
							Message: make sure contrology product variant id is correct, we could not retrieve it from the store
						`);
					}
					await addVariantsToCart({
						items: [
							{
								productId: variant.product.shopifyId,
								variantId,
								sku: variant.sku,
								quantity,
								productType: variant.product.productType,
								productTitle: variant.product.title,
								variantTitle: variant.title,
								price: variant.price,
								product: variant.product,
								variant,
							},
						],
					});
				})
				.catch(error => {
					window.console.error(error);
					setCartError({
						visible: true,
						errorEmail: error.message,
					});
				});
		}
   	}, [checkout.id]) // eslint-disable-line

	//	--------------------------------------------------
	//	Add mulitple products to checkout if params in URL
	//	--------------------------------------------------
	useEffect(() => {
		if (window.location.pathname === '/' && window.location.search && checkout.id) {
			try {
				const params = new URLSearchParams(window.location.search);
				const base64 = params.keys().next().value;
				const {
					ids, quantities, _isEducationOrder = '', _isMaterial = '',
				} = queryString.parse(atob(base64));

				if (!ids) return;

				const variantIds = ids
					.split(',')
					.map(id => `gid://shopify/ProductVariant/${ id.trim() }`);

				const variantQuantities = quantities.split(',');

				const educationOrderAttribs = _isEducationOrder
					.split(',')
					.map(value => ({
						key: '_is_education_order',
						value,
					}));

				const materialAttribs = _isMaterial
					.split(',')
					.map(value => ({
						key: '_is_material',
						value,
					}));

				getProductVariantsByIds(variantIds)
					.then(async variants => {
						if (variants?.filter(Boolean).length !== variantIds.length) {
							// not all variants were found, throw an error
							throw new Error(`
								Page: ${ window.location.href.replace('&', ' and ') } %0D%0A
								Message: not all variants were found in the store
							`);
						}
						const cartVariants = variants.map((variant, index) => {
							const customAttributes = [];

							const getProductTypeAttrib = () => {
								const prodType = variant.product.productType;
								if (prodType !== 'course_manual') return { key: '_product_type', value: prodType };
								if (variant.requiresShipping) return { key: '_product_type', value: 'Manual_Physical' };
								return { key: '_product_type', value: 'Manual_Digital' };
							};

							customAttributes.push(getProductTypeAttrib());
							if (educationOrderAttribs[index].value) customAttributes.push(educationOrderAttribs[index]);
							if (materialAttribs[index].value) customAttributes.push(materialAttribs[index]);

							return {
								productId: variant.product.storefrontId,
								variantId: variant.storefrontId,
								sku: variant.sku,
								quantity: parseInt(
									// ensure quantity is a number and fallback to 1 if not provided
									variantQuantities[index] ? variantQuantities[index].trim() : '1',
									10
								) || 1,
								customAttributes,
								productType: variant.product.productType,
								productTitle: variant.product.title,
								variantTitle: variant.title,
								price: variant.price,
							};
						});

						await addVariantsToCart({items: cartVariants});
					})
					.catch(error => {
						window.console.error(error);
						setCartError({
							visible: true,
							errorEmail: error.message,
						});
					});
			} catch (error) {
				// handle base64 decode error
				window.console.error(error);
			}
		}
   	}, [checkout.id]) // eslint-disable-line

	return {
		checkoutLoading,
		envoyClient,
		checkout,
		classCheckout,
		lastAdded,
		setCheckoutLoadingState,
		checkEventClassesAvailability,
		setCheckoutItem,
		setCheckoutAttributes,
		addVariantsToCart,
		updateLineItem,
		updateLineItems,
		removeLineItems,
	};
};

export default useCheckout;
